import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center font-mono text-sm transition-colors disabled:opacity-40 disabled:pointer-events-none border cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00ffcc99]",
	{
		variants: {
			variant: {
				default: "border-[#00ffcc] text-[#00ffcc] bg-[#00ffcc08] hover:bg-[#00ffcc14]",
				destructive: "border-red-500 text-red-400 bg-transparent hover:bg-red-950/40",
				ghost: "border-transparent text-[#9acfc4] hover:text-[#00ffcc] hover:border-[#00ffcc44] hover:bg-[#00ffcc08]",
				outline: "border-[#1b4d43] text-[#b4ded5] hover:border-[#00ffcc88] hover:text-[#00ffcc] hover:bg-[#00ffcc08]",
				active: "border-[#00ffcc] text-black bg-[#00ffcc] hover:bg-[#00ffcccc]",
			},
			size: {
				default: "h-8 px-3",
				sm: "h-7 px-2 text-xs",
				lg: "h-9 px-4",
				icon: "h-8 w-8",
			},
		},
		defaultVariants: { variant: "default", size: "default" },
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";
		return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
	},
);
Button.displayName = "Button";
